import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import {
  OrAvatarComponent,
  OrIconComponent,
  PageActionsComponent,
  PageLayoutComponent,
  TitileNavbarComponent
} from '@ui-components';
import { IonicModule } from '@ionic/angular';
import {
  gameLevelMapper,
  MODAL_TOKEN,
  ModalProvider,
  PLATFORM_TOKEN,
  PlatformProvider,
  preferredPositionMapper,
  PresentModalComponent
} from '@core';
import { switchMap, take, tap } from 'rxjs';
import {
  LineupsPlayerModel,
  LineupsQueryHandler,
  LineupsQueryResult,
  SaveLineupsCommandHandler
} from '../../application/handlers';
import { shuffleLineupsPosition } from '../../application/utils/shuffle-lineups-position.util';
import { AppPreferredPlayerPosition, GameLevelEnum } from '@core-http';

interface LineupsMetadata {
  readonly isSaved: boolean;
  readonly isHost: boolean;
}

@Component({
  selector: 'lib-lineups',
  templateUrl: './lineups.component.html',
  styleUrls: ['./lineups.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TitileNavbarComponent,
    IonicModule,
    OrAvatarComponent,
    PageLayoutComponent,
    PageActionsComponent,
    OrIconComponent
  ],
  providers: [SaveLineupsCommandHandler, LineupsQueryHandler]
})
export class LineupsComponent implements OnInit {
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly saveLineupsCommandHandler: SaveLineupsCommandHandler = inject(SaveLineupsCommandHandler);
  private readonly lineupsQueryHandler: LineupsQueryHandler = inject(LineupsQueryHandler);

  readonly players: WritableSignal<LineupsPlayerModel[]> = signal<LineupsPlayerModel[]>([]);
  readonly lineupsMetadata: WritableSignal<LineupsMetadata | null> = signal<LineupsMetadata | null>(null);
  readonly shouldShowLineups: WritableSignal<boolean> = signal<boolean>(false);
  readonly isPageLoaded: WritableSignal<boolean> = signal<boolean>(false);

  readonly gameLevelMapper: Record<GameLevelEnum, string> = gameLevelMapper;
  readonly preferredPositionMapper: Record<AppPreferredPlayerPosition, string> = preferredPositionMapper;
  readonly isiOS: boolean = this.platformProvider.isiOS;

  readonly splitTeamSize: Signal<number> = computed(() => {
    const players: LineupsPlayerModel[] = this.players();
    if (!players.length) return 0;
    return +(players.length / 2).toFixed();
  });

  ngOnInit(): void {
    this.loadData();
  }

  doReorder(event: CustomEvent): void {
    const currentPlayers: LineupsPlayerModel[] = [...this.players()];
    const itemMove: LineupsPlayerModel = currentPlayers.splice(event.detail.from, 1)[0];
    currentPlayers.splice(event.detail.to, 0, itemMove);
    this.players.set(currentPlayers);

    event.detail.complete();
  }

  shufflePlayers(): void {
    const newList: LineupsPlayerModel[] = shuffleLineupsPosition(this.players());
    this.players.set(newList);
    this.shouldShowLineups.set(true);
  }

  save(): void {
    const playerIds: string[] = this.players().map((player: LineupsPlayerModel) => player.playerId);
    this.saveLineupsCommandHandler
      .save(playerIds)
      .pipe(take(1))
      .subscribe(() => this.lineupsSavedModal());
  }

  private loadData(): void {
    this.modalProvider
      .showLoading$()
      .pipe(
        switchMap(() => this.lineupsQueryHandler.getLineups()),
        take(1),
        tap((lineups: LineupsQueryResult) => {
          this.lineupsMetadata.set({
            isSaved: lineups.isLineupsSaved,
            isHost: lineups.isHost
          });
          this.players.set(lineups.players);
        }),
        tap(() => this.modalProvider.dismissLoading$()),
        tap(() => this.isPageLoaded.set(true))
      )
      .subscribe();
  }

  private lineupsSavedModal(): void {
    this.modalProvider.showModal$({
      component: PresentModalComponent,
      componentProps: {
        header: 'Losowanie składów',
        message: 'Składy zostaly zapisane!',
        btnTxt: 'Zamknij'
      },
      cssClass: 'present-modal'
    });
  }
}
